import type { ReactNode } from 'react'
import { useLocale } from '../../locale/LocaleContext.js'

// EditorTrackSection — one-line track authoring toolbar for existing-route edit.
// GPX file actions stay visible on the left; the 「上传 GPX | 手绘」 switch stays
// on the right so switching to hand-draw does not make replacement/reverse
// controls jump to a different vertical position.

export type TrackMode = 'upload' | 'draw'

export interface EditorTrackSectionProps {
  mode: TrackMode
  onModeChange: (mode: TrackMode) => void
  upload: ReactNode
  draw: ReactNode
  disabled?: boolean
}

export function EditorTrackSection({
  mode,
  onModeChange,
  upload,
  draw,
  disabled = false,
}: EditorTrackSectionProps) {
  const { t } = useLocale()
  const segments: { key: TrackMode; label: string; testId: string }[] = [
    {
      key: 'upload',
      label: t('editor.trackSection.upload'),
      testId: 'editor-track-section-upload',
    },
    { key: 'draw', label: t('editor.trackSection.draw'), testId: 'editor-track-section-draw' },
  ]

  return (
    <div className="space-y-2" data-testid="editor-track-section">
      <div className="flex flex-wrap items-center gap-2" data-testid="editor-track-section-toolbar">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{upload}</div>
        <div
          className="ml-auto inline-flex overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)]"
          role="group"
          aria-label={t('editor.create.modeNav')}
        >
          {segments.map((segment) => {
            const active = mode === segment.key
            return (
              <button
                key={segment.key}
                type="button"
                data-testid={segment.testId}
                aria-pressed={active}
                disabled={disabled}
                onClick={() => {
                  if (!active) onModeChange(segment.key)
                }}
                className={
                  active
                    ? 'h-7 whitespace-nowrap bg-[var(--xr-brand-primary)] px-3 text-xs font-medium text-[var(--xr-brand-primary-ink)]'
                    : 'h-7 whitespace-nowrap bg-[var(--xr-bg-surface)] px-3 text-xs font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:cursor-not-allowed disabled:opacity-60'
                }
              >
                {segment.label}
              </button>
            )
          })}
        </div>
      </div>
      {mode === 'draw' && <div>{draw}</div>}
    </div>
  )
}
